import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { Container, Label, Message, Segment, Table } from 'semantic-ui-react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';

import { listAddons } from '@plone/volto/actions/addons/addons';
import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import { useClient } from '@plone/volto/hooks/client/useClient';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import Toolbar from '@plone/volto/components/manage/Toolbar/Toolbar';
import Error from '@plone/volto/components/theme/Error/Error';
import backSVG from '@plone/volto/icons/back.svg';

// Id do pacote no @addons — é a chave do add-on nesta instalação, não o título.
const ADDON_ID = 'ministerio.publico.federal';

const messages = defineMessages({
  back: { id: 'Back', defaultMessage: 'Voltar' },
  title: {
    id: 'MPF profile version',
    defaultMessage: 'Versão do site (MPF)',
  },
});

type UpgradeInfo = {
  required?: boolean;
  available?: boolean;
  installedVersion?: string;
  newVersion?: string;
};

type Addon = {
  id: string;
  title?: string;
  version?: string;
  upgrade_info?: UpgradeInfo;
};

const VersaoProfile = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const isClient = useClient();

  const installedAddons = useSelector(
    (state: any) => state.addons?.installedAddons as Addon[] | undefined,
  );
  const error = useSelector((state: any) => state.addons?.error);

  useEffect(() => {
    dispatch(listAddons());
  }, [dispatch]);

  if (error) {
    return <Error error={error} />;
  }

  if (!installedAddons) {
    return null;
  }

  const addon = installedAddons.find((el) => el.id === ADDON_ID);

  if (!addon) {
    return (
      <Container id="mpf-versao-profile-page">
        <Helmet title={intl.formatMessage(messages.title)} />
        <Segment.Group raised>
          <Segment className="primary">
            {intl.formatMessage(messages.title)}
          </Segment>
          <Segment>
            <Message warning>
              <FormattedMessage
                id="MPF addon not found"
                defaultMessage="O pacote {id} não foi encontrado entre os add-ons instalados."
                values={{ id: <code>{ADDON_ID}</code> }}
              />
            </Message>
          </Segment>
        </Segment.Group>
      </Container>
    );
  }

  const info = addon.upgrade_info ?? {};
  // `required` = versão de profile do banco difere da do código → há upgrade
  // steps pendentes a rodar. `available` cobre o caso raro de haver steps mas o
  // banco já estar na versão de topo (nunca deveria divergir de `required`).
  const pendente = Boolean(info.required || info.available);

  return (
    <Container id="mpf-versao-profile-page">
      <Helmet title={intl.formatMessage(messages.title)} />
      <Segment.Group raised>
        <Segment className="primary">
          {intl.formatMessage(messages.title)}
        </Segment>
        <Segment>
          {pendente ? (
            <Message warning>
              <Message.Header>
                <FormattedMessage
                  id="MPF upgrade pending"
                  defaultMessage="Upgrade pendente"
                />
              </Message.Header>
              <FormattedMessage
                id="MPF upgrade pending detail"
                defaultMessage="O banco está na versão de profile {instalada} e o código traz a {codigo}. Rode os upgrade steps para aplicar as versões pendentes (Add-ons → Atualizar, ou o upgrade do deploy)."
                values={{
                  instalada: <strong>{info.installedVersion}</strong>,
                  codigo: <strong>{info.newVersion}</strong>,
                }}
              />
            </Message>
          ) : (
            <Message positive>
              <Message.Header>
                <FormattedMessage
                  id="MPF upgrade up to date"
                  defaultMessage="Site em dia"
                />
              </Message.Header>
              <FormattedMessage
                id="MPF upgrade up to date detail"
                defaultMessage="A versão de profile aplicada no banco é a mesma do código."
              />
            </Message>
          )}

          <Table celled padded columns="2">
            <Table.Body>
              <Table.Row>
                <Table.Cell>
                  <FormattedMessage
                    id="Package version"
                    defaultMessage="Versão do pacote"
                  />
                </Table.Cell>
                <Table.Cell>{addon.version ?? '—'}</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>
                  <FormattedMessage
                    id="Applied profile version"
                    defaultMessage="Versão de profile aplicada (banco)"
                  />
                </Table.Cell>
                <Table.Cell>{info.installedVersion ?? '—'}</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>
                  <FormattedMessage
                    id="Code profile version"
                    defaultMessage="Versão de profile no código"
                  />
                </Table.Cell>
                <Table.Cell>{info.newVersion ?? '—'}</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>
                  <FormattedMessage id="Status" defaultMessage="Situação" />
                </Table.Cell>
                <Table.Cell>
                  {pendente ? (
                    <Label color="orange">
                      <FormattedMessage
                        id="MPF upgrade pending"
                        defaultMessage="Upgrade pendente"
                      />
                    </Label>
                  ) : (
                    <Label color="green">
                      <FormattedMessage
                        id="MPF upgrade up to date"
                        defaultMessage="Site em dia"
                      />
                    </Label>
                  )}
                </Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table>
        </Segment>
      </Segment.Group>
      {isClient &&
        createPortal(
          <Toolbar
            pathname={pathname}
            hideDefaultViewButtons
            inner={
              <UniversalLink href="/controlpanel" className="item">
                <Icon
                  name={backSVG}
                  aria-label={intl.formatMessage(messages.back)}
                  className="contents circled"
                  size="30px"
                  title={intl.formatMessage(messages.back)}
                />
              </UniversalLink>
            }
          />,
          document.getElementById('toolbar'),
        )}
    </Container>
  );
};

export default VersaoProfile;
